import { useState, useCallback } from 'react';
import { backInStockApi } from '../api/backInStock';
import type { BackInStockSettings, BackInStockSubscription } from '../api/backInStock';

export function useBackInStock() {
  const [settings, setSettings] = useState<BackInStockSettings | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [subscriptions, setSubscriptions] = useState<BackInStockSubscription[]>([]);
  const [subsTotal, setSubsTotal] = useState(0);
  const [subsPage, setSubsPage] = useState(1);
  const [subsLastPage, setSubsLastPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await backInStockApi.show();
      setSettings(res.data.data.settings);
      setPendingCount(res.data.data.pending_count);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load back-in-stock settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(async (next: BackInStockSettings) => {
    setSaving(true);
    setError(null);
    try {
      const res = await backInStockApi.update(next);
      setSettings(res.data.data);
      return res.data.data;
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save back-in-stock settings.');
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const fetchSubscriptions = useCallback(async (page = 1) => {
    try {
      const res = await backInStockApi.subscriptions(page);
      setSubscriptions(res.data.data);
      setSubsTotal(res.data.meta.total);
      setSubsLastPage(res.data.meta.last_page);
      setSubsPage(page);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load subscriptions.');
    }
  }, []);

  return {
    settings, pendingCount, subscriptions, subsTotal, subsPage, subsLastPage,
    loading, saving, error, fetchSettings, save, fetchSubscriptions, setError,
  };
}
